import * as z from 'zod/mini';
import { identifierSchema } from '../naming.ts';
import {
  idSchema,
  nameSchema,
  shortTextSchema,
  sortOrderSchema,
  timestampSchema,
} from './common.ts';

// A space holds racks, items, and drops. Most are interior rooms; an
// external location is the same in every way but stands for something
// mounted outside — a pole, a pedestal, a wall — so it reads and draws as
// external rather than as a room.
export const ROOM_SPACE_TYPES = ['interior', 'external'] as const;
export type RoomSpaceType = (typeof ROOM_SPACE_TYPES)[number];
export const roomSpaceTypeSchema = z.enum(ROOM_SPACE_TYPES);
export const ROOM_SPACE_TYPE_LABELS: Readonly<Record<RoomSpaceType, string>> = {
  interior: 'Interior room',
  external: 'External location',
};

export const roomSchema = z.object({
  id: idSchema,
  propertyId: idSchema,
  parentRoomId: z.nullable(idSchema),
  name: z.string(),
  /** Short path segment; blank only in a cache made before migration 0051. */
  identifier: z._default(z.string(), ''),
  spaceType: roomSpaceTypeSchema,
  /**
   * The floor of the property this room sits on (ROADMAP D1, migration 0065),
   * or null while it has not been placed on one. The free-text `floor` label
   * below is what rooms used before floors were a level; both are readable
   * side by side until every room has been moved over.
   */
  floorId: z._default(z.nullable(idSchema), null),
  /** The free-text storey label ("Ground", "2"); read where `floorId` is null. */
  floor: z.string(),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Room = z.infer<typeof roomSchema>;

export const roomInputSchema = z.object({
  name: nameSchema,
  identifier: z.optional(identifierSchema),
  spaceType: z._default(roomSpaceTypeSchema, 'interior'),
  /** A floor of the room's own property; the routes refuse any other (D1). */
  floorId: z._default(z.nullable(idSchema), null),
  floor: shortTextSchema,
});
export type RoomInput = z.infer<typeof roomInputSchema>;

export const roomPatchSchema = z.partial(roomInputSchema);
export type RoomPatch = z.infer<typeof roomPatchSchema>;
